import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { feedingRequestsApi } from '../utils/api';
import { handleApiError, formatDate } from '../utils/helpers';

const RequestedFeedingsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Fetch open feeding requests
  useEffect(() => {
    fetchRequests();
  }, [currentPage, pageSize]);
  
  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the new API method for open feeding requests
      const response = await feedingRequestsApi.getOpenFeedingRequests(currentPage, pageSize);
      console.log('Open feeding requests response:', response);
      
      // Handle the case when the API returns an array directly
      const requestsData = Array.isArray(response) ? response : (response.data || []);
      console.log('Processed requests data:', requestsData);
      
      setRequests(requestsData);
      setTotalPages(Math.ceil((response.totalCount || requestsData.length) / pageSize));
    } catch (error) {
      console.error('Error fetching feeding requests:', error);
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };
  
  // Handle filter change
  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setCurrentPage(0); // Reset to first page when filter changes
  };
  
  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  
  // Render pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="pagination">
        <button
          className="pagination-button"
          onClick={() => handlePageChange(0)}
          disabled={currentPage === 0}
        >
          &laquo;
        </button>
        
        <button
          className="pagination-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 0}
        >
          &lsaquo;
        </button>
        
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            className={`pagination-button ${currentPage === i ? 'active' : ''}`}
            onClick={() => handlePageChange(i)}
          >
            {i + 1}
          </button>
        ))}
        
        <button
          className="pagination-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages - 1}
        >
          &rsaquo;
        </button>
        
        <button
          className="pagination-button"
          onClick={() => handlePageChange(totalPages - 1)}
          disabled={currentPage === totalPages - 1}
        >
          &raquo;
        </button>
      </div>
    );
  };
  
  return (
    <div className="feeding-requests-page">
      <div className="page-header">
        <h1 className="page-title">My Pending Requests</h1>
        <p className="page-description">
          Track your open feeding requests that are waiting to be fulfilled by feeders
        </p>
      </div>
      
      {/* Filter and actions section */}
      <div className="actions-bar">
        <div className="filters-toggle">
          <button 
            className="button text" 
            onClick={toggleFilters}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="error-message">
          <p>{error}</p>
          <button 
            className="button"
            onClick={fetchRequests}
          >
            Try Again
          </button>
        </div>
      )}
      
      {/* Empty state */}
      {!loading && !error && (!requests || requests.length === 0) && (
        <div className="empty-state">
          <div className="empty-state-icon">🍽️</div>
          <h2 className="empty-state-title">No pending requests</h2>
          <p className="empty-state-description">
            You don't have any pending feeding requests. Browse our cats and make a request!
          </p>
          <Link to="/dashboard/cats" className="button">
            Browse Cats
          </Link>
        </div>
      )}
      
      {/* Requests list */}
      {!loading && !error && requests && requests.length > 0 && (
        <div className="requests-list">
          {requests.map((request, index) => {
            console.log(`Rendering request ${index}:`, request);
            
            if (!request || !request.cat) {
              console.error(`Invalid request data at index ${index}:`, request);
              return null;
            }
            
            return (
              <div key={request.id || index} className="request-item">
                <div className="request-cat-info">
                  <img 
                    src={(request.cat.catPhotos && request.cat.catPhotos.length > 0) 
                      ? request.cat.catPhotos[0].path 
                      : '/cat-placeholder.png'} 
                    alt={request.cat.name || 'Cat'} 
                    className="request-cat-image"
                    onError={(e) => {
                      console.log('Image failed to load, using fallback');
                      e.target.src = '/cat-placeholder.png';
                    }}
                  />
                  <div className="request-details">
                    <h3>{request.cat.name || 'Unknown Cat'}</h3>
                    <p className="request-date">
                      Requested on {request.createdAt 
                        ? new Date(request.createdAt).toLocaleDateString() 
                        : new Date().toLocaleDateString()}
                    </p>
                    <p className="request-status">
                      Status: <span className="status-pending">Pending</span>
                    </p>
                  </div>
                </div>
                <div className="request-actions">
                  <Link 
                    to={`/dashboard/cats/${request.cat.id}`} 
                    className="button secondary"
                  >
                    View Cat
                  </Link>
                </div>
              </div>
            );
          })}
          
          {/* Pagination */}
          {totalPages > 1 && renderPagination()}
        </div>
      )}
    </div>
  );
};

export default RequestedFeedingsPage; 